import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Z } from '../../styles/zLayers';
import { MAX_PALETTE_COLORS, useBrandPalette } from '../../contexts/BrandPaletteContext';
import { useExclusivePopover } from './overlay-controls/PopoverGroup';

// Default preset colors (fallback when no brand palette is available)
const DEFAULT_PRESETS = ['#2563eb', '#dc2626', '#16a34a', '#ca8a04', '#9333ea', '#0891b2'];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

interface ColorPickerPopoverProps {
  color: string;
  onChange: (color: string) => void;
  /**
   * Fires once per picker interaction instead of per tick: debounced ~400ms
   * behind onChange (a native color-input drag emits onChange continuously),
   * flushed immediately on preset click, popover close, and unmount. Put
   * network persistence here and keep onChange for live preview state — a
   * PATCH wired directly to onChange fires ~10+/sec during a drag (the July
   * 2026 event-storm pattern). Only valid #rrggbb values are committed, so
   * partial hex-input keystrokes never persist.
   */
  onChangeCommitted?: (color: string) => void;
  presetColors?: string[];
  triggerSize?: 'sm' | 'md' | 'tb' | 'lg';
  position?: 'above' | 'below' | 'auto';
  disabled?: boolean;
  title?: string;
  showHexInput?: boolean;
  /** Called when popover opens/closes - parent can use this to prevent hover-close */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Reusable color picker with popover containing presets and native color input.
 * Uses a portal to render the popover at document body level, so it won't be
 * affected by parent container visibility (e.g., hover-based toolbars).
 */
const ColorPickerPopover: React.FC<ColorPickerPopoverProps> = ({
  color,
  onChange,
  onChangeCommitted,
  presetColors,
  triggerSize = 'sm',
  position = 'above',
  disabled = false,
  title = 'Pick a color',
  showHexInput = false,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useExclusivePopover();
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const lastInteractionRef = useRef<number>(0);

  // Debounced-commit machinery for onChangeCommitted. Pending value + timer
  // live in refs so re-renders never drop or double-fire a commit; the
  // callback ref avoids stale closures without effect churn.
  const committedCbRef = useRef(onChangeCommitted);
  committedCbRef.current = onChangeCommitted;
  const pendingCommitRef = useRef<string | null>(null);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushCommit = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    if (pendingCommitRef.current !== null) {
      const pending = pendingCommitRef.current;
      pendingCommitRef.current = null;
      committedCbRef.current?.(pending);
    }
  }, []);

  const scheduleCommit = useCallback((newColor: string) => {
    // Only full #rrggbb values are committable (partial hex while typing is
    // fine for live preview, never for persistence).
    if (!committedCbRef.current || !HEX_RE.test(newColor)) return;
    pendingCommitRef.current = newColor;
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(flushCommit, 400);
  }, [flushCommit]);

  // Flush any pending commit when the popover closes (no-op when nothing is
  // pending, so the initial closed state and mount never emit) and on unmount.
  useEffect(() => {
    if (!isOpen) flushCommit();
  }, [isOpen, flushCommit]);
  useEffect(() => () => flushCommit(), [flushCommit]);

  // Brand palette (null when no provider is mounted → fall back to defaults).
  // Explicit presetColors prop always wins.
  const { colors: brandColors, saveColor: saveToBrandPalette } = useBrandPalette();
  const effectivePresets =
    presetColors ?? (brandColors && brandColors.length ? brandColors : null) ?? DEFAULT_PRESETS;
  const canSaveToPalette =
    brandColors !== null &&
    HEX_RE.test(color) &&
    !brandColors.includes(color.toLowerCase());
  const paletteFull = brandColors !== null && brandColors.length >= MAX_PALETTE_COLORS;

  // Size classes for trigger button
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    tb: 'w-6 h-6',  // toolbar — matches MiniPositionPicker height
    lg: 'w-8 h-8',
  };

  // Popover size based on trigger size
  const popoverSwatchSize = {
    sm: 'w-6 h-6',
    md: 'w-7 h-7',
    tb: 'w-7 h-7',
    lg: 'w-8 h-8',
  };

  const nativeInputSize = {
    sm: 'w-7 h-7',
    md: 'w-8 h-8',
    tb: 'w-8 h-8',
    lg: 'w-9 h-9',
  };

  // Notify parent when open state changes
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Calculate popover position
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return null;

    const rect = triggerRef.current.getBoundingClientRect();
    const popoverHeight = 44; // Approximate height

    if (position === 'below') {
      return {
        top: rect.bottom + 4,
        left: rect.left + rect.width / 2,
      };
    } else {
      return {
        top: rect.top - popoverHeight - 4,
        left: rect.left + rect.width / 2,
      };
    }
  }, [position]);

  // Update position on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const pos = calculatePosition();
      if (pos) setPopoverPosition(pos);
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, calculatePosition]);

  // Close on click outside - but only if not recently interacted with native picker
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      // If we interacted with the native color picker recently (it just closed),
      // don't close the popover yet - give 2000ms grace period
      const timeSinceLastInteraction = Date.now() - lastInteractionRef.current;
      if (timeSinceLastInteraction < 2000) {
        return;
      }

      const target = e.target as Node;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedPopover = popoverRef.current?.contains(target);

      if (!clickedTrigger && !clickedPopover) {
        setIsOpen(false);
        setPopoverPosition(null);
      }
    };

    // Small delay before adding listener to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setPopoverPosition(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen]);

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (disabled) return;

    if (!isOpen) {
      // Calculate position immediately when opening
      const pos = calculatePosition();
      if (pos) {
        setPopoverPosition(pos);
        setIsOpen(true);
      } else {
        // Fallback: use mouse position if trigger ref isn't available
        setPopoverPosition({
          top: e.clientY - 50,
          left: e.clientX,
        });
        setIsOpen(true);
      }
    } else {
      setIsOpen(false);
      setPopoverPosition(null);
    }
  }, [disabled, isOpen, calculatePosition, setIsOpen]);

  const handleColorChange = useCallback((newColor: string) => {
    lastInteractionRef.current = Date.now();
    onChange(newColor);
    scheduleCommit(newColor);
  }, [onChange, scheduleCommit]);

  const handlePresetClick = useCallback((presetColor: string) => {
    onChange(presetColor);
    // A preset click is a discrete choice — commit immediately, no debounce
    if (HEX_RE.test(presetColor)) {
      pendingCommitRef.current = presetColor;
      flushCommit();
    }
    // Don't close on preset click - let user compare colors
  }, [onChange, flushCommit]);

  const handleNativeInputInteraction = useCallback((_e?: React.SyntheticEvent) => {
    // Track that user is interacting with native picker
    lastInteractionRef.current = Date.now();
  }, []);

  return (
    <>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={disabled}
        className={`${sizeClasses[triggerSize]} rounded cursor-pointer border border-white/50 p-0 transition-all relative
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-white hover:scale-105'}
          ${isOpen ? 'ring-2 ring-blue-400 border-blue-400' : ''}`}
        style={{ backgroundColor: color, zIndex: 10 }}
        title={title}
      />

      {/* Popover - rendered in portal at document body level */}
      {isOpen && popoverPosition && createPortal(
        <div
          ref={popoverRef}
          data-overlay-popover=""
          className="rounded-lg p-2 flex items-center gap-2 shadow-xl border border-white/20 backdrop-blur-sm"
          style={{
            position: 'fixed',
            top: popoverPosition.top,
            left: popoverPosition.left,
            transform: 'translateX(-50%)',
            zIndex: Z.PICKER,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Native color input (provides browser eyedropper) */}
          <input
            type="color"
            value={color}
            onMouseDown={handleNativeInputInteraction}
            onClick={handleNativeInputInteraction}
            onFocus={handleNativeInputInteraction}
            onChange={(e) => handleColorChange(e.target.value)}
            onBlur={handleNativeInputInteraction}
            className={`${nativeInputSize[triggerSize]} rounded cursor-pointer border border-white/30 p-0 bg-transparent
              [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded [&::-webkit-color-swatch]:border-0`}
            title="Pick a custom color"
          />

          {/* Hex input (optional) */}
          {showHexInput && (
            <input
              type="text"
              value={color}
              onChange={(e) => {
                const v = e.target.value;
                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) handleColorChange(v);
              }}
              onBlur={(e) => {
                if (!/^#[0-9a-fA-F]{6}$/.test(e.target.value)) handleColorChange('#2563eb');
              }}
              className="w-[5.5rem] bg-white/10 text-white text-xs font-mono px-1.5 py-1 rounded
                border border-white/20 focus:outline-none focus:border-white/50 uppercase"
              maxLength={7}
              spellCheck={false}
            />
          )}

          {/* Preset color buttons (brand palette when available) */}
          {effectivePresets.map((presetColor) => (
            <button
              key={presetColor}
              type="button"
              onClick={() => handlePresetClick(presetColor)}
              className={`${popoverSwatchSize[triggerSize]} rounded-full border-2 transition-all
                ${color.toLowerCase() === presetColor.toLowerCase()
                  ? 'border-white scale-110'
                  : 'border-transparent hover:border-white/50 hover:scale-105'}`}
              style={{ backgroundColor: presetColor }}
              title={presetColor}
            />
          ))}

          {/* Save current color to the brand palette (only with a provider) */}
          {brandColors !== null && (
            <button
              type="button"
              onClick={() => {
                if (canSaveToPalette && !paletteFull) saveToBrandPalette(color);
              }}
              disabled={!canSaveToPalette || paletteFull}
              className={`${popoverSwatchSize[triggerSize]} rounded-full border border-dashed transition-all
                flex items-center justify-center text-white/70 text-xs leading-none
                ${!canSaveToPalette || paletteFull
                  ? 'border-white/20 opacity-40 cursor-default'
                  : 'border-white/50 hover:border-white hover:scale-105'}`}
              title={
                paletteFull
                  ? 'Palette full — manage colors in the brand menu'
                  : canSaveToPalette
                  ? 'Save to brand palette'
                  : 'Already in brand palette'
              }
            >
              +
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default ColorPickerPopover;
